// "Vocals only" for a cover with several voices. The jammer keeps no
// vocals-only mix and convertedUrl is one voice's whole track, so build the
// real acapella here: each voice's track stitched by the cover's voiceTimes
// (the studio's loadStems), encoded to AAC in an .m4a (WAV where the browser
// can't encode AAC), and handed to the browser as a download.
import { canEncodeAac, m4aOf } from "./aac";
import { loadStems } from "./audio";
import { voiceTracksOf } from "../../utils/voiceTracks";

/* eslint-disable @typescript-eslint/no-explicit-any */

function wavOf(buffer: AudioBuffer): Blob {
  const ch = buffer.numberOfChannels,
    rate = buffer.sampleRate,
    n = buffer.length;
  const out = new DataView(new ArrayBuffer(44 + n * ch * 2));
  const str = (o: number, t: string) =>
    [...t].forEach((c, i) => out.setUint8(o + i, c.charCodeAt(0)));
  str(0, "RIFF");
  out.setUint32(4, 36 + n * ch * 2, true);
  str(8, "WAVEfmt ");
  out.setUint32(16, 16, true);
  out.setUint16(20, 1, true);
  out.setUint16(22, ch, true);
  out.setUint32(24, rate, true);
  out.setUint32(28, rate * ch * 2, true);
  out.setUint16(32, ch * 2, true);
  out.setUint16(34, 16, true);
  str(36, "data");
  out.setUint32(40, n * ch * 2, true);
  const data = Array.from({ length: ch }, (_, c) => buffer.getChannelData(c));
  let o = 44;
  for (let i = 0; i < n; i++)
    for (let c = 0; c < ch; c++) {
      const v = Math.max(-1, Math.min(1, data[c][i]));
      out.setInt16(o, v < 0 ? v * 0x8000 : v * 0x7fff, true);
      o += 2;
    }
  return new Blob([out.buffer], { type: "audio/wav" });
}

/** True when "Vocals only" needs building (several voices) rather than convertedUrl. */
export const acapellaNeedsBuilding = (conversion: any) =>
  !!voiceTracksOf(conversion);

export async function downloadAcapella(
  conversion: any,
  name: string,
): Promise<void> {
  const voices = voiceTracksOf(conversion);
  if (!voices) throw new Error("Not a cover with several voices");
  const ctx = new AudioContext();
  try {
    const { vocals } = await loadStems(ctx, null, null, voices);
    if (!vocals) throw new Error("Couldn't load the voices");
    let blob: Blob, ext: string;
    if (
      await canEncodeAac(
        vocals.sampleRate,
        Math.min(2, vocals.numberOfChannels),
      )
    ) {
      const channels = Array.from(
        { length: Math.min(2, vocals.numberOfChannels) },
        (_, c) => vocals.getChannelData(c),
      );
      blob = new Blob(
        [await m4aOf({ sampleRate: vocals.sampleRate, channels })],
        { type: "audio/mp4" },
      );
      ext = "m4a";
    } else {
      blob = wavOf(vocals);
      ext = "wav";
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name.replace(/[\\/:*?"<>|]+/g, "").trim() || "jammable"} (vocals).${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  } finally {
    ctx.close().catch(() => {});
  }
}
